// CRM 2.0 Banking › CF NOC (M10-F3, DC-73 / DC-143). Tabs New requests · Sent to lender ·
// Received with counts; filters Project, Wing, Lender and search (changing tab clears them).
//   New requests   select → Download CF Excel (one file per lender; the rows move to Sent)
//   Sent to lender waiting days (amber / red from the settings), "Booking is at",
//                  Upload CF (CF number + PDF), Re-download Excel, Undo sent
//   Received       CF number, date, document, who uploaded it; Undo received
// Buttons show only with the role's Banking › CF NOC Download / Upload; the server checks too.
// Super Admin: "Add booking to CF list" (a booking whose request was not made, e.g. CF NOC
// switched on after the AMR). Backend: routes/crm2Cf.js.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel, MenuItem, Modal, TextField } from "@mui/material";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost, successTost } from "../../../Services/Toast";
import {
  getCfList, exportCfExcel, redownloadCfExcel, undoCfSent, markCfReceived, undoCfReceived, getCfDocument,
  getCfProjects, getCfBookings, requestCf, cfError,
} from "../../../Services/Crm2Cf";
import css from "./CfNocList.module.css";

const TABS = [["new", "New requests"], ["sent", "Sent to lender"], ["received", "Received"]];
const NO_FILTERS = { projectId: "", wingId: "", lenderId: "", search: "" };
const inputSx = { "& .MuiInputBase-root": { fontSize: "1.4rem" }, "& .MuiInputLabel-root": { fontSize: "1.4rem" }, "& .MuiFormHelperText-root": { fontSize: "1.2rem" } };
const menuSx = { fontSize: "1.4rem" };
const primary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const secondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };

const fmtDate = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDay = (key) => (key ? new Date(`${key}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const unitText = (r) => [r.projectName, r.wingName, r.unitNo].filter(Boolean).join(" · ");
const daysText = (n) => (n === null || n === undefined ? "—" : `${n} day${n === 1 ? "" : "s"}`);
const WAIT_CHIP = { ok: css.chip, warn: `${css.chip} ${css.chipWarn}`, error: `${css.chip} ${css.chipError}` };

function Pop({ title, onClose, busy, children, footer }) {
  return (
    <Modal open onClose={(_, reason) => reason !== "backdropClick" && !busy && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={css.modal}>
        <div className={css.modalHeader}>
          <span>{title}</span>
          <span className={css.modalClose} onClick={() => !busy && onClose()} tabIndex={0} role="button" aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
        </div>
        <div className={css.modalContent}>{children}</div>
        <div className={css.modalFooter}>{footer}</div>
      </Box>
    </Modal>
  );
}

const CfNocList = ({ canDownload, canUpload, isAdmin }) => {
  const [tab, setTab] = useState("new");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [search, setSearch] = useState("");
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState("waiting"); // Sent to lender: waiting | bookingIsAt
  const [upload, setUpload] = useState(null); // row
  const [undo, setUndo] = useState(null); // { row, kind: 'sent' | 'received' }
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    getCfList({ tab, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) })
      .then((d) => {
        setList(d);
        setSelected((s) => s.filter((id) => d.rows.some((r) => r.allotmentPaymentId === id)));
      })
      .catch(async (err) => setError((await cfError(err)).message))
      .finally(() => setLoading(false));
  }, [tab, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Search waits until typing stops.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === search.trim() ? f : { ...f, search: search.trim() })), 400);
    return () => clearTimeout(t);
  }, [search]);

  const changeTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setFilters(NO_FILTERS); // DC-143: changing tab clears the filters
    setSearch("");
    setSelected([]);
  };

  const rows = useMemo(() => {
    const r = list?.rows || [];
    if (tab !== "sent" || sortBy === "waiting") return r;
    return [...r].sort((a, b) => String(a.bookingIsAt).localeCompare(String(b.bookingIsAt)));
  }, [list, tab, sortBy]);
  const anyFilter = Object.values(filters).some(Boolean) || search;
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.allotmentPaymentId));

  async function act(fn, okText) {
    setBusy(true);
    try {
      const out = await fn();
      if (okText) successTost(typeof okText === "function" ? okText(out) : okText);
      load();
      return out;
    } catch (err) {
      const e = await cfError(err);
      errorTost(e.message);
      load();
      throw e;
    } finally {
      setBusy(false);
    }
  }

  const download = () =>
    act(() => exportCfExcel(selected), (o) => `${o.sent} request${o.sent === 1 ? "" : "s"} moved to Sent to lender${o.skipped ? ` · ${o.skipped} skipped (no longer in New requests)` : ""}`)
      .then(() => setSelected([]))
      .catch(() => {});

  const opts = list?.options || { projects: [], wings: [], lenders: [] };
  const filterSelect = (key, label, options) => (
    <TextField select size="small" label={label} className={css.select} sx={{ ...inputSx, minWidth: "16rem" }} value={filters[key]}
      InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
      onChange={(e) => setFilters({ ...filters, [key]: e.target.value, ...(key === "projectId" ? { wingId: "" } : {}) })}>
      <MenuItem value="" sx={menuSx}>All</MenuItem>
      {options.map((o) => <MenuItem key={o.value} value={o.value} sx={menuSx}>{o.label}</MenuItem>)}
    </TextField>
  );

  return (
    <div className={css.wrap}>
      <div className={css.bar}>
        <div className={css.tabs} role="tablist">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} className={`${css.tab} ${tab === key ? css.tabOn : ""}`} onClick={() => changeTab(key)}>
              {label}
              <span className={css.count}>{list?.counts?.[key] ?? "–"}</span>
            </button>
          ))}
        </div>
        <span className={css.grow} />
        {isAdmin && <Button style={secondary} btnName="+ Add booking to CF list" clicked={() => setAdding(true)} />}
      </div>

      <div className={css.bar}>
        {filterSelect("projectId", "Project", opts.projects)}
        {filterSelect("wingId", "Wing", opts.wings)}
        {filterSelect("lenderId", "Lender", opts.lenders)}
        <TextField size="small" label="Search unit, applicant, CF no." className={css.select} sx={{ ...inputSx, minWidth: "22rem" }} value={search} onChange={(e) => setSearch(e.target.value)} />
        {anyFilter && <button type="button" className={css.link} onClick={() => { setFilters(NO_FILTERS); setSearch(""); }}>Clear all</button>}
        <span className={css.grow} />
        {tab === "sent" && (
          <TextField select size="small" label="Sort" className={css.select} sx={{ ...inputSx, minWidth: "20rem" }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="waiting" sx={menuSx}>Waiting longest first</MenuItem>
            <MenuItem value="bookingIsAt" sx={menuSx}>Booking is at</MenuItem>
          </TextField>
        )}
        {tab === "new" && canDownload && (
          <Button style={primary} btnName={`Download CF Excel${selected.length ? ` (${selected.length})` : ""}`} disable={!selected.length || busy} isLoading={busy} clicked={download} />
        )}
      </div>

      {error ? (
        <div className={css.empty}>{error} <button type="button" className={css.link} onClick={load}>Try again</button></div>
      ) : !list ? (
        <div className={css.empty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={css.empty}>{anyFilter ? "No requests match the filters." : tab === "new" ? "No new CF NOC requests." : tab === "sent" ? "Nothing is waiting with a lender." : "No CF NOC received yet."}</div>
      ) : (
        <div className={css.tableCard} style={{ opacity: loading ? 0.6 : 1 }}>
          <table className={css.table}>
            <thead>
              {tab === "new" && (
                <tr>
                  {canDownload && (
                    <th style={{ width: "1%" }}>
                      <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selected.length > 0} inputProps={{ "aria-label": "Select all" }}
                        onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.allotmentPaymentId) : [])} />
                    </th>
                  )}
                  <th>Unit</th><th>Applicants</th><th>RERA AMR</th><th>Lender</th><th>Request created</th>
                </tr>
              )}
              {tab === "sent" && <tr><th>Unit</th><th>Applicants</th><th>Lender</th><th>Sent</th><th>Waiting</th><th>Booking is at</th><th /></tr>}
              {tab === "received" && <tr><th>Unit</th><th>Lender</th><th>CF number</th><th>CF date</th><th>CF document</th><th>Received</th><th /></tr>}
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.allotmentPaymentId}>
                  {tab === "new" && canDownload && (
                    <td>
                      <Checkbox size="small" checked={selected.includes(r.allotmentPaymentId)} inputProps={{ "aria-label": `Select ${r.unitNo}` }}
                        onChange={(e) => setSelected((s) => (e.target.checked ? [...s, r.allotmentPaymentId] : s.filter((x) => x !== r.allotmentPaymentId)))} />
                    </td>
                  )}
                  <td><b>{r.unitNo}</b><div className={css.muted}>{[r.projectName, r.wingName].filter(Boolean).join(" · ")}</div></td>
                  {tab !== "received" && <td>{r.applicants.join(", ") || "—"}</td>}
                  {tab === "new" && (
                    <>
                      <td>
                        {r.amr.receiptNo || "—"}
                        <div className={css.muted}>{[r.amr.amount !== "" && r.amr.amount != null ? `₹${Number(r.amr.amount).toLocaleString("en-IN")}` : null, r.amr.date, r.amr.utr && `UTR ${r.amr.utr}`].filter(Boolean).join(" · ") || "Details come with the AMR"}</div>
                      </td>
                      <td>{r.lenderName || <span className={`${css.chip} ${css.chipError}`}>No lender</span>}</td>
                      <td>{fmtDate(r.requestedAt)}<div className={css.muted}>{daysText(r.waitingDays)} ago</div></td>
                    </>
                  )}
                  {tab === "sent" && (
                    <>
                      <td>{r.lenderName || <span className={`${css.chip} ${css.chipError}`}>No lender</span>}</td>
                      <td>{fmtDate(r.sentAt)}{r.sentByName && <div className={css.muted}>by {r.sentByName}</div>}</td>
                      <td><span className={WAIT_CHIP[r.waitLevel] || css.chip}>{daysText(r.waitingDays)}</span></td>
                      <td>{r.bookingIsAt}</td>
                      <td>
                        <div className={css.actions}>
                          {canUpload && <button type="button" className={css.link} disabled={busy} onClick={() => setUpload(r)}>Upload CF</button>}
                          {canDownload && r.excelBatchId && (
                            <button type="button" className={css.link} disabled={busy} onClick={() => act(() => redownloadCfExcel(r.excelBatchId)).catch(() => {})}>Excel</button>
                          )}
                          {canDownload && <button type="button" className={css.link} disabled={busy} onClick={() => setUndo({ row: r, kind: "sent" })}>Undo sent</button>}
                        </div>
                      </td>
                    </>
                  )}
                  {tab === "received" && (
                    <>
                      <td>{r.lenderName || "—"}</td>
                      <td><b>{r.cfNumber}</b>{r.remarks && <div className={css.muted}>{r.remarks}</div>}</td>
                      <td>{fmtDay(r.cfDate)}</td>
                      <td>
                        {r.hasDocument ? (
                          <button type="button" className={css.link} onClick={() => getCfDocument(r.allotmentPaymentId).then((d) => window.open(d.url, "_blank", "noopener")).catch(async (err) => errorTost((await cfError(err)).message))}>
                            {r.documentName || "CF NOC.pdf"}
                          </button>
                        ) : "—"}
                      </td>
                      <td>{fmtDate(r.receivedAt)}<div className={css.muted}>{r.source === "email" ? "From email" : r.receivedByName ? `Uploaded by ${r.receivedByName}` : ""}</div></td>
                      <td>
                        <div className={css.actions}>
                          {canUpload && <button type="button" className={css.link} disabled={busy} onClick={() => setUndo({ row: r, kind: "received" })}>Undo</button>}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {upload && (
        <UploadPop row={upload} onClose={() => setUpload(null)}
          onSave={(body) => act(() => markCfReceived(upload.allotmentPaymentId, body), (o) => `CF NOC received for ${upload.unitNo}${o.emailed ? " · CRM emailed" : ""}`).then(() => setUpload(null))} />
      )}
      {undo && (
        <ReasonPop
          title={undo.kind === "sent" ? `Undo sent · ${undo.row.unitNo}` : `Undo received · ${undo.row.unitNo}`}
          text={undo.kind === "sent" ? "The request moves back to New requests and can be downloaded again." : "The CF number and document are taken off the request and it moves back to Sent to lender. The uploaded PDF is kept in the history."}
          onClose={() => setUndo(null)}
          onSave={(reason) => act(() => (undo.kind === "sent" ? undoCfSent : undoCfReceived)(undo.row.allotmentPaymentId, reason), undo.kind === "sent" ? "Moved back to New requests" : "Moved back to Sent to lender").then(() => setUndo(null))} />
      )}
      {adding && <AddBookingPop onClose={() => setAdding(false)} onAdded={() => { setAdding(false); load(); }} />}
    </div>
  );
};

function UploadPop({ row, onClose, onSave }) {
  const [cfNumber, setCfNumber] = useState("");
  const [cfDate, setCfDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const today = new Date().toLocaleDateString("en-CA");

  const pick = (f) => {
    setErrors((e) => ({ ...e, file: undefined }));
    if (!f) return setFile(null);
    if (f.type !== "application/pdf" && !/\.pdf$/i.test(f.name)) return setErrors((e) => ({ ...e, file: "Only a PDF file can be uploaded" }));
    if (f.size > 5 * 1024 * 1024) return setErrors((e) => ({ ...e, file: "The PDF must be 5 MB or smaller" }));
    setFile(f);
  };

  const save = async () => {
    const e = {};
    if (!cfNumber.trim()) e.cfNumber = "Required";
    if (!file) e.file = "Attach the CF document (PDF)";
    if (cfDate && cfDate > today) e.cfDate = "Cannot be in the future";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = () => reject(new Error("Could not read the file"));
        reader.readAsDataURL(file);
      });
      await onSave({ cfNumber: cfNumber.trim(), cfDate: cfDate || null, remarks: remarks.trim() || null, fileName: file.name, fileBase64 });
    } catch (err) {
      if (err?.fields) setErrors(err.fields);
      setSaving(false);
    }
  };

  return (
    <Pop title={`Upload CF · ${unitText(row)}`} onClose={onClose} busy={saving}
      footer={<><Button style={secondary} btnName="Cancel" clicked={() => !saving && onClose()} /><Button style={primary} btnName="Save" isLoading={saving} disable={saving} clicked={save} /></>}>
      <p className={css.muted} style={{ margin: 0 }}>Lender: {row.lenderName || "No lender"} · sent {fmtDate(row.sentAt)}</p>
      <TextField size="small" label="CF number *" sx={inputSx} value={cfNumber} onChange={(e) => setCfNumber(e.target.value)} error={!!errors.cfNumber} helperText={errors.cfNumber} inputProps={{ maxLength: 60 }} />
      <div>
        <div className={css.label}>CF document (PDF) *</div>
        <div className={css.fileRow}>
          <input type="file" accept="application/pdf,.pdf" onChange={(e) => pick(e.target.files?.[0])} />
          {file && <span className={css.muted}>{file.name}</span>}
        </div>
        {errors.file && <p className={css.error}>{errors.file}</p>}
      </div>
      <TextField size="small" type="date" label="CF date" sx={inputSx} InputLabelProps={{ shrink: true }} inputProps={{ max: today }} value={cfDate} onChange={(e) => setCfDate(e.target.value)} error={!!errors.cfDate} helperText={errors.cfDate} />
      <TextField size="small" label="Remarks" multiline minRows={2} sx={inputSx} value={remarks} onChange={(e) => setRemarks(e.target.value)} inputProps={{ maxLength: 500 }} error={!!errors.remarks} helperText={errors.remarks} />
    </Pop>
  );
}

function ReasonPop({ title, text, onClose, onSave }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    if (reason.trim().length < 3) return setError("Give a reason (at least 3 characters)");
    setSaving(true);
    try {
      await onSave(reason.trim());
    } catch {
      setSaving(false);
    }
  };
  return (
    <Pop title={title} onClose={onClose} busy={saving}
      footer={<><Button style={secondary} btnName="Cancel" clicked={() => !saving && onClose()} /><Button style={primary} btnName="Confirm" isLoading={saving} disable={saving} clicked={save} /></>}>
      <p className={css.muted} style={{ margin: 0 }}>{text}</p>
      <TextField size="small" label="Reason *" multiline minRows={2} sx={inputSx} value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }} error={!!error} helperText={error} inputProps={{ maxLength: 300 }} />
    </Pop>
  );
}

const STATUS_TEXT = { PENDING: "New request", SENT: "Sent to lender", RECEIVED: "Received", NOT_NEEDED: "Not needed" };

function AddBookingPop({ onClose, onAdded }) {
  const [projects, setProjects] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [bookings, setBookings] = useState(null);
  const [id, setId] = useState("");
  const [recheck, setRecheck] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCfProjects().then(setProjects).catch(async (err) => errorTost((await cfError(err)).message));
  }, []);
  useEffect(() => {
    setBookings(null);
    setId("");
    if (projectId) getCfBookings(projectId).then(setBookings).catch(async (err) => errorTost((await cfError(err)).message));
  }, [projectId]);

  const chosen = bookings?.find((b) => b.allotmentPaymentId === id);
  const blocked = chosen && (!chosen.hasAgreementForm || (chosen.cfNocStatus && !(chosen.cfNocStatus === "NOT_NEEDED" && recheck)));

  const save = async () => {
    setSaving(true);
    try {
      const out = await requestCf(id, recheck);
      if (out.changed) {
        successTost(out.status === "PENDING" ? `CF request made for ${chosen.unitNo} (${out.decision?.lenderName || "no lender"})` : `${chosen.unitNo}: CF NOC is not needed (${out.decision?.reason || "project setting"})`);
        onAdded();
      } else {
        errorTost(`${chosen.unitNo} already has a CF status: ${STATUS_TEXT[out.status] || out.status}`);
        setSaving(false);
      }
    } catch (err) {
      errorTost((await cfError(err)).message);
      setSaving(false);
    }
  };

  return (
    <Pop title="Add booking to CF list" onClose={onClose} busy={saving}
      footer={<><Button style={secondary} btnName="Cancel" clicked={() => !saving && onClose()} /><Button style={primary} btnName="Add" isLoading={saving} disable={!chosen || blocked || saving} clicked={save} /></>}>
      <p className={css.muted} style={{ margin: 0 }}>
        For a CRM 2.0 booking whose CF request was not made (for example CF NOC switched on after its AMR). The project's CF NOC setting decides whether it is needed and the lender.
      </p>
      <TextField select size="small" label="Project" sx={inputSx} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
        {(projects || []).map((p) => <MenuItem key={p.projectId} value={p.projectId} sx={menuSx}>{p.projectName}</MenuItem>)}
      </TextField>
      {projectId && (
        <TextField select size="small" label="Booking" sx={inputSx} value={id} onChange={(e) => setId(e.target.value)} disabled={!bookings}>
          {(bookings || []).map((b) => (
            <MenuItem key={b.allotmentPaymentId} value={b.allotmentPaymentId} sx={menuSx}>
              {[b.wingName, b.unitNo].filter(Boolean).join(" · ")} — {b.cfNocStatus ? STATUS_TEXT[b.cfNocStatus] : b.hasAgreementForm ? "no CF status" : "no agreement form"}
            </MenuItem>
          ))}
        </TextField>
      )}
      {chosen?.cfNocStatus === "NOT_NEEDED" && (
        <FormControlLabel control={<Checkbox size="small" checked={recheck} onChange={(e) => setRecheck(e.target.checked)} />}
          label={<span style={{ fontSize: "1.4rem" }}>Marked "not needed" before: decide again from today's CF NOC setting</span>} />
      )}
      {chosen && !chosen.hasAgreementForm && <p className={css.error}>This booking has no agreement form yet (Agreement KYC not submitted), so a CF request cannot be made.</p>}
      {chosen && chosen.cfNocStatus && chosen.cfNocStatus !== "NOT_NEEDED" && <p className={css.error}>This booking already has a CF request ({STATUS_TEXT[chosen.cfNocStatus]}).</p>}
    </Pop>
  );
}

export default CfNocList;
